/**
 * `deploy` — the host-agnostic **deploy glue** for adding a combatant to an
 * encounter — the seam between a *source* (a roster pilot-mech, or an NPC class + tier)
 * and the `add_combatant` command the encounter room dispatches.
 *
 * The model is **pure and unit-testable** — `deployMech` / `deployNpc` run the real
 * `spawnMech` / `spawnNpc` (which freeze the combatant `base` at spawn — the
 * frozen-base discipline lives there, never reinvented here) and, on success, return
 * the `add_combatant` command to dispatch; on a spawn rejection they return the
 * `{ ok:false, error }` arm verbatim so the host surfaces it inline. No React, no ws,
 * no `dispatch` — the host wires those (the same split `scenePreset.ts` uses for
 * terrain). A frameless mech is a clean `spawnMech` `{ ok:false }`, and an NPC source
 * is only ever present when a pack is loaded (the host gates the section on
 * `compendium.count("npc_class") > 0` — absence disables the section and says why).
 *
 * **Never hand-build a `Combatant`** — deploy goes through the spawn adapters so the
 * freeze-base + `controllerSlotId` capture (mech: `pilot.owner`; npc: `null`, i.e. GM-owned,
 * unless the GM picks a born owner — `DeployNpcArgs.controllerSlotId`) stay correct.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import type { AddCombatantCommand, EncounterBatchCommand, LoadoutViolation, MechData, NpcWear, PilotData, Side } from "@lancer-vtt/domain";
import { loadoutViolations } from "@lancer-vtt/domain";
import {
  computeNpcOptionalBudgets,
  resolveOriginFeatures,
  spawnMech,
  spawnNpc,
  spawnPilot,
  classifyMechanization,
  type LoadedOverlay,
  type NpcClassBlock,
  type NpcOptionalBudget,
  type NpcTemplateBlock,
  type NpcTier,
  type RawNpcFeature,
} from "@lancer-vtt/rules";
import { type Result } from "@lancer-vtt/shared";

/** Mint a combatant id (the spawn adapters take a caller-minted id, like the roster commands). */
export function deployId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * The outcome of a deploy attempt: the `add_combatant` command on the value arm, or
 * the spawn rejection message on the error arm. `Result`-shaped exactly like the
 * `SpawnResult` it wraps — a spawn `{ ok:false }` propagates through unchanged.
 */
export type DeployResult = Result<AddCombatantCommand>;

// ── mech deploy ────────────────────────────────────────────────────────────────

/** One row in the Mechs picker section — a `(pilot, mech)` pair flattened from the roster. */
export type MechDeployRow = {
  pilot: PilotData;
  mechIdx: number;
  mechId: string;
  mechName: string;
  /** `null` ⇒ frameless: the row stays listed but its deploy is disabled (mirrors `spawnMech`). */
  frameId: string | null;
  /** Uploaded portrait URL, threaded through so `resolveMechArtUrl` sees the full three-tier source. */
  image?: string;
  /**
   * All loadout invariant violations on this mech's active loadout (`loadoutViolations`) —
   * empty when legal. **Presentation only**: a non-empty list never disables the row (deploy
   * stays legal at every tier) — the host renders a hazard sub-tag off this field while the deploy button
   * stays enabled and clicking it still spawns via {@link deployMech} unchanged. A
   * frameless mech's empty `loadouts` array degenerates this to `[]` (nothing to violate
   * yet), same as a mech with no active loadout.
   */
  violations: LoadoutViolation[];
  /**
   * The ids of this mech's equipped systems that reach nothing in this client — empty when
   * everything the mech carries is
   * mechanized, and **always empty when no overlay was passed** (see the third argument of
   * {@link mechDeployRows}). Presentation only, exactly like {@link violations}: the deploy
   * button stays enabled and the spawn is unchanged. This is the GM-side backstop, not the
   * primary surface — a talent is chosen on the PILOT sheet days before the fight, so the sheet chip
   * is the earliest honest moment and this row is the last one.
   */
  fictionOnly: string[];
};

/**
 * Flatten the roster to one row per `(pilot, mech)` pair — the Mechs picker's
 * source. A frameless mech (`frameId == null`) is kept in the list so the GM sees it,
 * but the host disables its deploy (mirroring `spawnMech`'s frameless rejection as an
 * affordance). Each row also carries its {@link LoadoutViolation}s (`violations`) —
 * derived off the same `loadoutViolations` selector every other surface reads (mirror, never
 * re-derive) — so the picker can flag an invalid-but-deployable mech without a second walk of the
 * loadout.
 * Pure: a stable mapping from `(pilots, compendium[, overlay])` → rows.
 *
 * **`overlay` is OPT-IN, and its absence means "say nothing" — never "assume the worst"**
 * — the same convention `roster-view.ts`'s `pilotView` uses for the CLI. Omitted, every row's
 * {@link MechDeployRow.fictionOnly} is `[]` and
 * the host renders no sub-tag. The alternative — defaulting to the intrinsics-only
 * `SEED_OVERLAY` — would classify nearly the whole corpus as fiction-only and paint the
 * hazard across every row on the picker, which is precisely the cry-wolf failure the whole
 * vector-aware verdict exists to avoid. A caller whose overlay is still loading passes
 * `undefined`, not a partial overlay.
 */
export function mechDeployRows(pilots: PilotData[], compendium: Compendium, overlay?: LoadedOverlay): MechDeployRow[] {
  return pilots.flatMap((p) =>
    p.mechs.map((m, idx) => ({
      pilot: p,
      mechIdx: idx,
      mechId: m.id,
      mechName: m.name,
      frameId: m.frameId,
      image: m.image,
      violations: loadoutViolations(m, p, compendium),
      fictionOnly: overlay === undefined ? [] : unmechanizedSystemsOf(m, compendium, overlay),
    })),
  );
}

/**
 * The equipped systems on a mech's ACTIVE loadout that classify `unmechanized`, deduped and in
 * loadout order. Deliberately scoped to equipped systems, matching the CLI marker's own scope —
 * a talent's integrated grant never lands in `loadout.systems` and so is invisible here too —
 * a known, deliberate gap.
 */
function unmechanizedSystemsOf(mech: MechData, compendium: Compendium, overlay: LoadedOverlay): string[] {
  const systems = mech.loadouts[mech.activeLoadoutIndex ?? 0]?.systems ?? [];
  const seen = new Set<string>();
  for (const id of systems) {
    if (classifyMechanization({ entryId: id, compendium, overlay }) === "unmechanized") seen.add(id);
  }
  return [...seen];
}

export type DeployMechArgs = {
  actorId: string;
  pilot: PilotData;
  mechIdx: number;
  compendium: Compendium;
  /**
   * The merged action overlay, threaded straight into `spawnMech`. **Present ⇒ the spawn freezes a
   * Full-Repair-grain affordance RECORD (`base.affordances`, cap `{grain:"scene", max, limited:true}`)
   * for every OVERLAY-MINTED action id whose authored `action` clause carries a `limitedUses` facet** — `ms_custom_paint_job:0` and its kind, which
   * have no compendium `actions[]`/`deployables[]`/`tg_limited` for the ordinary freeze arms to
   * read (`projects/track-damage-event-family.md` D2/D3). Absent ⇒ no such cap is frozen and the
   * ability is uncapped, exactly as `SpawnMechArgs.overlay`'s own doc says.
   *
   * **The cap freezes ONCE, at spawn, and nothing later repairs it**, so passing an overlay that
   * has not finished loading is not a transient wrong answer — it is a permanently uncapped mech
   * for the rest of that combatant's life. That is what {@link mechDeployHoldReason} exists to
   * prevent at the picker; this argument stays optional because the e2e/unit call sites that
   * deliberately spawn plain mechs have no overlay in scope and want none.
   */
  overlay?: LoadedOverlay;
  /** Override the minted combatant id (tests pass a deterministic one). */
  combatantId?: string;
};

/**
 * The user-facing reason a mech deploy is held while the action overlay is still loading. Kept
 * beside {@link mechDeployHoldReason} so the copy and the condition live in one place.
 */
export const OVERLAY_PENDING_HOLD =
  "Loading content — mech deploy is held for a moment. A mech deployed right now would spawn with no limited-use charges (Custom Paint Job and its kind) for the rest of the fight, and nothing later repairs it.";

/**
 * The user-facing reason a mech deploy stays held because the content overlay failed to load. The
 * failure itself is appended by {@link mechDeployHoldReason}.
 */
export const OVERLAY_FAILED_HOLD =
  "Content failed to load, so mech deploy is held: a mech deployed now would spawn without its limited-use charges (Custom Paint Job and its kind). Reload the page to retry";

/**
 * Why the Mechs section must **hold** its deploys right now, or `null` when one may proceed.
 * Mirrors {@link templateProhibitReason}'s shape: a non-null return is the reason the host shows,
 * and the host is what enforces it.
 *
 * **This is a real block, not an advisory** — the one place in this file where the picker refuses
 * to act rather than flagging and proceeding, and the difference is that this failure is permanent
 * and invisible. `mechDeployRows`' `violations`/`fictionOnly` are *presentation* (an invalid or
 * fiction-only mech still deploys, and the GM can see and undo what they got); a mech spawned
 * before the overlay settles looks completely normal and is simply missing charges forever. Custom
 * Paint Job would be usable an unlimited number of times, silently, with nothing on screen to say
 * so and no later event to fix it.
 *
 * **The loading hold is bounded and self-clearing**: `useCompendium` resolves `loadingOverlay` to
 * `false` in a `.finally`, so it settles even when the sidecar fetch fails outright. **A failed
 * load then keeps the hold closed** (`overlayError`, {@link OVERLAY_FAILED_HOLD}): the overlay it
 * settled on lacks the failed sidecar's content, so deploying against it is the same permanent,
 * silent loss. The hold names the failure; a reload that loads cleanly lifts it.
 *
 * Deliberately scoped to the MECH source only. `spawnPilot`/`spawnNpc` take no overlay, so neither
 * freezes an overlay-minted cap either way and neither has anything to wait for.
 */
export function mechDeployHoldReason(loadingOverlay: boolean, overlayError: string | null = null): string | null {
  if (loadingOverlay) return OVERLAY_PENDING_HOLD;
  return overlayError !== null ? `${OVERLAY_FAILED_HOLD} (${overlayError})` : null;
}

/**
 * Spawn a combatant from a roster pilot-mech and build the `add_combatant` command to
 * dispatch. Runs the real `spawnMech` (freezes the base); a frameless / unresolved
 * mech returns the `{ ok:false }` arm verbatim. The combatant lands UNPLACED
 * (`pos: null`) — the select-then-click-empty-cell flow places it.
 */
export function deployMech(args: DeployMechArgs): DeployResult {
  const { actorId, pilot, mechIdx, compendium, overlay, combatantId = deployId() } = args;
  const spawn = spawnMech({ combatantId, pilotData: pilot, mechIdx, compendium, ...(overlay ? { overlay } : {}) });
  if (!spawn.ok) return spawn;
  return { ok: true, value: { type: "add_combatant", actorId, combatant: spawn.value } };
}

// ── pilot deploy (on-foot, no mech) ─────────────────────────────────────────────

/** One row in the Pilots picker section — one row per roster pilot (no mech dimension). */
export type PilotDeployRow = {
  pilot: PilotData;
  pilotId: string;
  pilotName: string;
  callsign: string;
};

/**
 * Flatten the roster to one row per pilot — the Pilots picker's source. Unlike
 * {@link mechDeployRows} there's no mech to flatten over: a direct on-foot deploy
 * (`spawnPilot`) always succeeds (no frame-resolution dependency), so every row is
 * always deployable. Pure: a stable mapping from pilots → rows.
 */
export function pilotDeployRows(pilots: PilotData[]): PilotDeployRow[] {
  return pilots.map((p) => ({
    pilot: p,
    pilotId: p.id,
    pilotName: p.name,
    callsign: p.callsign || p.name,
  }));
}

export type DeployPilotArgs = {
  actorId: string;
  pilot: PilotData;
  compendium: Compendium;
  /** Override the minted combatant id (tests pass a deterministic one). */
  combatantId?: string;
};

/**
 * Spawn a **direct on-foot pilot combatant** from a roster pilot (no mech — a plain
 * `spawnPilot`) and build the `add_combatant` command to dispatch. Runs the real
 * `spawnPilot` (freezes the pilot's own `ComputedPilot.combat` block as the base — no
 * frame resolution, so this always succeeds, unlike {@link deployMech}). The combatant
 * lands UNPLACED (`pos: null`) — the select-then-click-empty-cell flow places it.
 */
export function deployPilot(args: DeployPilotArgs): DeployResult {
  const { actorId, pilot, compendium, combatantId = deployId() } = args;
  const spawn = spawnPilot({ combatantId, pilotData: pilot, compendium });
  if (!spawn.ok) return spawn;
  return { ok: true, value: { type: "add_combatant", actorId, combatant: spawn.value } };
}

// ── npc deploy ─────────────────────────────────────────────────────────────────

/** One row in the NPCs picker section — an NPC class the GM can deploy at tier 1/2/3. */
export type NpcDeployOption = {
  id: string;
  name: string;
  npcClass: NpcClassBlock;
};

/**
 * The compendium's NPC classes as picker options. Empty when no NPC `.lcp` is loaded
 * (free Lancer data ships zero `npc_class` entries — the host then disables the NPCs
 * section behind the absence banner, Decision 4). Sorted by display name.
 */
export function npcDeployOptions(compendium: Compendium): NpcDeployOption[] {
  return compendium
    .list("npc_class")
    .map((c) => {
      const npcClass = c as unknown as NpcClassBlock;
      const id = npcClass.id ?? (c.id as string);
      return { id, name: npcClass.name ?? id, npcClass };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type DeployNpcArgs = {
  actorId: string;
  npcClass: NpcClassBlock;
  tier: NpcTier;
  /** The feature source `spawnNpc` freezes the class's `base_features` from (weapons + display features). */
  compendium: Compendium;
  /**
   * Applied template ids, in application order — the picker has already resolved these to real ids
   * (it renders off {@link npcTemplateOptions}, no free-text ref resolution needed client-side,
   * unlike the CLI's `--template <ref>`). Omit/`[]` ⇒ no templates. Threaded straight through to
   * `spawnNpc` exactly like the CLI's `add --npc` does — never re-derived here.
   */
  templateIds?: string[];
  /**
   * GM-chosen optional feature ids (class + applied-template pools), in pick order — same
   * honest-degrade discipline as {@link templateIds}. Omit/`[]` ⇒ no optional picks.
   */
  optionalFeatureIds?: string[];
  /**
   * The owner of record the NPC is born with (`unit-control-assignment` Phase E) — `null`/omitted
   * ⇒ GM-owned. Threaded straight to `spawnNpc`, like the CLI's `add --npc --slot <id>`.
   */
  controllerSlotId?: string | null;
  /** The side it is born on — omitted ⇒ `"npc"`. The CLI twin is `add --npc --side pc|npc`. */
  allegiance?: Side;
  /** Deploy as a keyed allied NPC seeded from its carried wear — threaded straight to `spawnNpc`. */
  persistent?: { key: string; wear?: NpcWear };
  /** The loaded overlay, threaded straight to `spawnNpc` (it names which actions of a Limited feature spend a charge). */
  overlay?: LoadedOverlay;
  /** Override the minted combatant id (tests pass a deterministic one). */
  combatantId?: string;
};

/**
 * Spawn a combatant from an NPC class block at a tier (+ optional templates/optional-feature
 * picks) and build the `add_combatant` command. Runs the real `spawnNpc` (freezes the merged
 * class+template feature list into `base.weapons` + `base.npcFeatures` from the `compendium`,
 * and the resolved template ids onto `Combatant.templateIds` — decision 10); a malformed/empty
 * class returns the `{ ok:false }` arm verbatim. The combatant lands UNPLACED, GM-owned and
 * NPC-side unless `controllerSlotId`/`allegiance` say otherwise (Phase E: born owned).
 */
export function deployNpc(args: DeployNpcArgs): DeployResult {
  const {
    actorId,
    npcClass,
    tier,
    compendium,
    templateIds = [],
    optionalFeatureIds = [],
    controllerSlotId,
    allegiance,
    persistent,
    overlay,
    combatantId = deployId(),
  } = args;
  const spawn = spawnNpc({
    combatantId,
    npcClass,
    tier,
    compendium,
    templateIds,
    optionalFeatureIds,
    ...(controllerSlotId !== undefined ? { controllerSlotId } : {}),
    ...(allegiance !== undefined ? { allegiance } : {}),
    ...(persistent ? { persistent } : {}),
    ...(overlay ? { overlay } : {}),
  });
  if (!spawn.ok) return spawn;
  return { ok: true, value: { type: "add_combatant", actorId, combatant: spawn.value } };
}

// ── npc template + optional-feature picker sources ─────────────────────────────

/** One row in the picker's template multi-select — an NPC template the GM may apply to the chosen class. */
export type NpcTemplateOption = {
  id: string;
  name: string;
  template: NpcTemplateBlock;
};

/**
 * The compendium's NPC templates as picker options. Empty when no NPC `.lcp` carries any
 * `npc_template` entries (a v2-only or minimal pack) — the picker then simply shows no
 * template section (a class with zero templates deploys base-only, pre-Phase-3 behavior).
 * Sorted by display name, mirroring {@link npcDeployOptions}.
 */
export function npcTemplateOptions(compendium: Compendium): NpcTemplateOption[] {
  return compendium
    .list("npc_template")
    .map((t) => {
      const template = t as unknown as NpcTemplateBlock;
      const id = template.id ?? (t.id as string);
      return { id, name: template.name ?? id, template };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Whether picking `candidate` alongside the currently-SELECTED templates trips
 * `prohibitTemplates` in either direction — advisory/affordance tier, never a wall. A non-null
 * return is the reason string the picker
 * shows greyed/annotated next to `candidate`; the option stays fully clickable regardless (a GM
 * can deploy the "incompatible" combo on purpose, exactly like the CLI warns-and-deploys-anyway).
 * `null` when there's no conflict (including when `candidate` is itself already selected — a
 * template never conflicts with itself).
 */
export function templateProhibitReason(candidate: NpcTemplateOption, selected: NpcTemplateOption[]): string | null {
  const conflicts = selected.filter(
    (s) =>
      s.id !== candidate.id &&
      ((candidate.template.prohibitTemplates ?? []).includes(s.id) ||
        (s.template.prohibitTemplates ?? []).includes(candidate.id)),
  );
  if (conflicts.length === 0) return null;
  return `advisory: marked incompatible with ${conflicts.map((c) => c.name).join(", ")} (prohibitTemplates) — deploying this combo is still allowed`;
}

/** One section of the picker's per-source optional-feature list: the class's own list, or one applied template's own list. */
export type NpcOptionalSection = {
  sourceId: string;
  sourceName: string;
  pool: RawNpcFeature[];
  budget: NpcOptionalBudget;
};

/**
 * Build the DEPLOY picker's per-source optional-feature sections for a class + its
 * currently-selected templates at a tier (`npc-deploy-composition` decision 8, reusing
 * {@link computeNpcOptionalBudgets}/{@link resolveOriginFeatures} verbatim — never re-derived):
 * the class's own optional pool first, then each selected template's own pool, in selection
 * order, each paired with its budget. Pure; the picker renders one section per entry with a
 * budget chip and a checkbox per feature.
 */
export function npcOptionalSections(args: {
  npcClass: NpcClassBlock;
  templates: NpcTemplateBlock[];
  tier: NpcTier;
  compendium: Compendium;
}): NpcOptionalSection[] {
  const { npcClass, templates, tier, compendium } = args;
  const budgets = computeNpcOptionalBudgets({ npcClass, templates, tier });
  const classPool = resolveOriginFeatures({
    originId: npcClass.id,
    explicitBase: npcClass.base_features,
    explicitOptional: npcClass.optional_features,
    compendium,
  }).optional;
  const sections: NpcOptionalSection[] = [
    {
      sourceId: npcClass.id ?? "?",
      sourceName: npcClass.name ?? npcClass.id ?? "class",
      pool: classPool,
      budget: budgets.classBudget,
    },
  ];
  templates.forEach((t, i) => {
    const pool = resolveOriginFeatures({
      originId: t.id,
      explicitBase: t.base_features,
      explicitOptional: t.optional_features,
      compendium,
    }).optional;
    const budget = budgets.templateBudgets[i] ?? { sourceId: t.id ?? "?", min: 0, max: 0 };
    sections.push({ sourceId: t.id ?? "?", sourceName: t.name ?? t.id ?? "template", pool, budget });
  });
  return sections;
}

/** How many of `optionalFeatureIds` fall within `pool` — a section's current pick count for its budget chip. */
export function countPicksIn(pool: RawNpcFeature[], optionalFeatureIds: string[]): number {
  const ids = new Set(pool.map((f) => f.id).filter((id): id is string => id !== undefined));
  return optionalFeatureIds.filter((id) => ids.has(id)).length;
}

// ── deploy-concealed (fog-of-war Decision 9) ───────────────────────────────────

/**
 * Wrap a deploy's `add_combatant` into the atomic **deploy-concealed** batch:
 * `batch[add_combatant, set_concealed]` — ONE apply, ONE projected broadcast, so a
 * concealed ambush unit never flashes on a player client between the add and the
 * conceal (the deploy-flash problem). The
 * combatant id is already minted by the spawn adapter, so the second command is
 * built purely from the first. GM-only in effect: `set_concealed` is gated GM-only
 * server-side, so the whole batch rejects for a non-GM — the host only offers the
 * option to a GM viewer (affordance mirror, never the wall).
 */
export function concealedDeploy(add: AddCombatantCommand): EncounterBatchCommand {
  return {
    type: "batch",
    commands: [
      add,
      { type: "set_concealed", actorId: add.actorId, combatantId: add.combatant.id, concealed: true },
    ],
  };
}
